// services/bookingService.js - COMPLETE WITH PROPER ERROR HANDLING
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:8081';

// ============================================
// JWT TOKEN UTILITIES
// ============================================

const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('❌ JWT decode error:', error);
    return null;
  }
};

const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
      console.error('❌ Invalid token structure');
      return true;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = decoded.exp < currentTime;
    
    if (isExpired) {
      console.error('❌ Token expired at:', new Date(decoded.exp * 1000));
      console.error('❌ Current time:', new Date(currentTime * 1000));
    } else {
      const minutesUntilExpiry = Math.floor((decoded.exp - currentTime) / 60);
      console.log('✅ Token valid for', minutesUntilExpiry, 'minutes');
    }
    
    return isExpired;
  } catch (error) {
    console.error('❌ Token validation error:', error);
    return true;
  }
};

const handleAuthError = (message = 'Your session has expired. Please login again.') => {
  console.error('🚫 Authentication Error:', message);
  
  localStorage.removeItem('token');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('auth_user');
  
  alert(message);
  window.location.href = '/login';
};

// ============================================
// CENTRALIZED ERROR HANDLER
// ============================================

const handleApiError = (error, context = 'API call') => {
  console.error(`❌ ${context} failed:`, error);

  const errorData = error?.response?.data;
  const status = error?.response?.status;
  const message = errorData?.message || errorData?.error || error?.message;

  const exceptionPatterns = {
    'CouponNotFoundException': 'Coupon code not found or has expired',
    'CouponInactiveException': 'This coupon is currently inactive',
    'CouponQuotaExceededException': 'This coupon has reached its usage limit',
    'CouponNotYetValidException': 'This coupon is not yet valid',
    'CouponExpiredException': 'This coupon has expired',
    'CouponMinAmountException': message || 'Minimum order amount not met',
    'CouponNotEligibleException': 'You are not eligible for this coupon',
    'CouponAlreadyUsedException': 'You have already used this coupon',
    'CouponValidationException': message || 'Coupon validation failed',
    'ActiveBookingExistsException': 'Selected bike is already booked for the chosen dates/time',
    'BookingNotFoundException': 'Booking not found',
    'InvalidBookingException': 'Invalid booking details provided',
    'UnauthorizedException': 'Please login to continue',
    'ForbiddenException': 'You do not have permission to perform this action'
  };

  let userMessage = message;
  if (message) {
    for (const [exceptionClass, friendlyMessage] of Object.entries(exceptionPatterns)) {
      if (message.includes(exceptionClass) || errorData?.exception?.includes(exceptionClass)) {
        userMessage = friendlyMessage;
        break;
      }
    }
  }

  const statusMessages = {
    400: userMessage || 'Invalid request. Please check your input',
    401: 'Please login to continue',
    403: 'You do not have permission to perform this action',
    404: userMessage || 'Requested resource not found',
    409: userMessage || 'This action conflicts with existing data',
    422: userMessage || 'Validation failed. Please check your input',
    500: 'Server error. Please try again later',
    503: 'Service temporarily unavailable'
  };

  const structuredError = {
    message: statusMessages[status] || userMessage || 'An unexpected error occurred',
    status: status,
    response: error?.response,
    originalError: error,
    code: errorData?.errorCode || errorData?.code
  };

  const errorToThrow = new Error(structuredError.message);
  errorToThrow.response = structuredError.response;
  errorToThrow.status = structuredError.status;
  errorToThrow.code = structuredError.code;

  throw errorToThrow;
};

// ============================================
// STORAGE & TOKEN DETECTION
// ============================================

const detectTokenKey = () => {
  const possibleKeys = ['token', 'auth_token', 'authToken', 'jwt_token', 'accessToken'];
  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value && value.trim() !== '') {
      return key;
    }
  }
  return 'token';
};

const STORAGE_KEYS = {
  get TOKEN() {
    return detectTokenKey();
  },
  USER: 'auth_user'
};

// ============================================
// AXIOS INSTANCE SETUP
// ============================================

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

apiClient.interceptors.request.use(
  (config) => {
    const tokenKey = STORAGE_KEYS.TOKEN;
    const token = localStorage.getItem(tokenKey);
    
    if (token) {
      if (isTokenExpired(token)) {
        console.error('❌ Token expired - blocking request');
        handleAuthError('Your session has expired. Please login again.');
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Valid token attached to request');
    } else {
      console.warn('⚠️ No token found in storage');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    console.log('🔄 Attempting to refresh token...');
    const response = await axios.post(`${API_BASE_URL}/api/auth/refreshtoken`, {
      refreshToken: refreshToken
    });

    const { accessToken, token } = response.data;
    const newToken = accessToken || token;
    
    if (!newToken) {
      throw new Error('No token in refresh response');
    }
    
    localStorage.setItem('token', newToken);
    console.log('✅ Token refreshed successfully');
    return newToken;
    
  } catch (error) {
    console.error('❌ Token refresh failed:', error);
    handleAuthError('Session expired. Please login again.');
    throw error;
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log('🔄 401 error - attempting token refresh...');
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ Token refresh failed - redirecting to login');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// FORMAT DURATION HELPER
// ============================================

const formatDuration = (hours) => {
  if (!hours || hours <= 0) return 'N/A';

  if (hours < 24) {
    const roundedHours = Math.round(hours);
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);

  if (remainingHours === 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  return `${days}d ${remainingHours}h`;
};

// ============================================
// BOOKING SERVICE METHODS
// ============================================

export const bookingService = {
  
  createBooking: async (bookingData) => {
    try {
      const tokenKey = STORAGE_KEYS.TOKEN;
      const token = localStorage.getItem(tokenKey);
      
      if (!token) {
        handleAuthError('Please login to continue booking');
        throw new Error('Authentication required');
      }

      if (isTokenExpired(token)) {
        handleAuthError('Your session has expired. Please login again.');
        throw new Error('Token expired');
      }

      const decoded = decodeJWT(token);
      console.log('🔐 Token Info:', {
        customerId: decoded?.customerId,
        expiresAt: decoded?.exp ? new Date(decoded.exp * 1000) : 'Unknown'
      });

      const requiredFields = {
        vehicleId: 'Vehicle ID',
        startDate: 'Start date',
        endDate: 'End date',
        pickupTime: 'Pickup time',
        dropoffTime: 'Dropoff time'
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!bookingData[field]) {
          throw new Error(`${label} is required`);
        }
      }

      if (!bookingData.charges && !bookingData.subtotal) {
        throw new Error('Rental charges are required');
      }
      
      if (!bookingData.finalAmount) {
        throw new Error('Final amount is required');
      }

      const charges = bookingData.charges || bookingData.subtotal || 0;
      const finalAmount = bookingData.finalAmount || 0;

      if (charges <= 0) {
        throw new Error('Invalid rental charges amount');
      }

      if (finalAmount <= 0) {
        throw new Error('Invalid final amount');
      }

      const start = new Date(`${bookingData.startDate}T${bookingData.pickupTime}`);
      const end = new Date(`${bookingData.endDate}T${bookingData.dropoffTime}`);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error('Invalid date or time format');
      }

      const totalHours = (end - start) / (1000 * 60 * 60);

      if (totalHours <= 0) {
        throw new Error('Dropoff time must be after pickup time');
      }

      if (totalHours < 1) {
        throw new Error('Minimum rental duration is 1 hour');
      }

      if (totalHours > 720) {
        throw new Error('Maximum rental duration is 30 days');
      }

      const requestPayload = {
        vehicleId: parseInt(bookingData.vehicleId, 10),
        startDate: `${bookingData.startDate}T${bookingData.pickupTime}:00`,
        endDate: `${bookingData.endDate}T${bookingData.dropoffTime}:00`,
        startDate1: bookingData.startDate,
        endDate1: bookingData.endDate,
        charges: parseFloat(bookingData.charges || bookingData.subtotal) || 0,
        gst: parseFloat(bookingData.gst) || 0,
        totalCharges: parseFloat(bookingData.totalCharges) || parseFloat(bookingData.charges || bookingData.subtotal),
        finalAmount: parseFloat(bookingData.finalAmount) || 0,
        advanceAmount: parseFloat(bookingData.advanceAmount || bookingData.deposit) || 0,
        totalHours: parseFloat(totalHours.toFixed(2)),
        additionalHours: 0,
        paymentType: bookingData.paymentType || 1,
        paymentStatus: bookingData.paymentStatus || 'PENDING',
        addressType: bookingData.addressType || 'Self Pickup',
        address: bookingData.address || '',
        deliveryType: bookingData.addressType === 'Delivery' ? 'Home Delivery' : null,
        pickupLocationId: parseInt(bookingData.pickupLocationId) || 1,
        dropLocationId: parseInt(bookingData.dropLocationId) || 1,
        couponCode: bookingData.couponCode || null,
        couponId: bookingData.couponCode ? (parseInt(bookingData.couponId) || 0) : 0,
        couponAmount: parseFloat(bookingData.couponAmount) || 0,
        additionalCharges: 0,
        additionalChargesDetails: null,
        deliveryCharges: 0,
        km: 0,
        lateFeeCharges: 0,
        lateEndDate: null,
        merchantTransactionId: null,
        transactionId: null,
        bookingStatus: bookingData.bookingStatus || 1
      };

      console.log('📋 Creating booking for vehicle ID:', bookingData.vehicleId);
      console.log('💰 Charges:', requestPayload.charges, 'Final:', requestPayload.finalAmount);
      
      const response = await apiClient.post('/api/booking-bikes/create', requestPayload);
      console.log('✅ Booking created successfully:', response.data);
      return response.data;

    } catch (error) {
      handleApiError(error, 'Create booking');
    }
  },

  getAllBookings: async () => {
    try {
      const response = await apiClient.get('/api/booking-bikes/allBooking');
      return response.data;
    } catch (error) {
      handleApiError(error, 'Fetch all bookings');
    }
  },

  getBookingsByCustomer: async (customerId, page = 0, size = 10, sortBy = 'latest') => {
    try {
      const response = await apiClient.get('/api/booking-bikes/by-customer', {
        params: { customerId, page, size, sortBy }
      });

      if (response.data && Array.isArray(response.data)) {
        response.data = response.data.map(booking => ({
          ...booking,
          durationDisplay: formatDuration(booking.totalHours)
        }));
      }

      return response.data;
    } catch (error) {
      handleApiError(error, 'Fetch customer bookings');
    }
  },

  getBookingById: async (id) => {
    try {
      const response = await apiClient.get(`/api/booking-bikes/getById/${id}`);

      if (response.data && response.data.totalHours) {
        response.data.durationDisplay = formatDuration(response.data.totalHours);
      }

      return response.data;
    } catch (error) {
      handleApiError(error, 'Fetch booking by ID');
    }
  },

  acceptBooking: async (bookingId) => {
    try {
      const response = await apiClient.post(`/api/booking-bikes/${bookingId}/accept`);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Accept booking');
    }
  },

  startTrip: async (bookingId, images, startTripKm) => {
    try {
      console.log('🚗 Starting trip:', bookingId);
      
      if (!bookingId) throw new Error('Booking ID is required');
      if (!images || images.length !== 4) throw new Error('All 4 bike images are required');
      if (!startTripKm || startTripKm <= 0) throw new Error('Valid kilometer reading is required');

      const formData = new FormData();
      images.forEach((image) => {
        if (image) formData.append('images', image);
      });
      formData.append('startTripKm', startTripKm.toString());

      const response = await apiClient.post(
        `/api/booking-bikes/${bookingId}/start`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      console.log('✅ Trip started:', response.data);
      return response.data;

    } catch (error) {
      handleApiError(error, 'Start trip');
    }
  },

  endTrip: async (bookingId, images, endTripKm) => {
    try {
      console.log('🏁 Ending trip:', bookingId);
      
      if (!bookingId) throw new Error('Booking ID is required');
      if (!images || images.length !== 4) throw new Error('All 4 bike images are required');
      if (!endTripKm || endTripKm <= 0) throw new Error('Valid kilometer reading is required');

      const formData = new FormData();
      images.forEach((image) => {
        if (image) formData.append('images', image);
      });
      formData.append('endTripKm', endTripKm.toString());

      const response = await apiClient.post(
        `/api/booking-bikes/${bookingId}/end`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      console.log('✅ Trip ended:', response.data);
      return response.data;

    } catch (error) {
      handleApiError(error, 'End trip');
    }
  },

  getVehicleDocuments: async (vehicleId) => {
    try {
      if (!vehicleId) throw new Error('Vehicle ID is required');
      const response = await apiClient.get(`/api/bikes/view-documents/${vehicleId}`);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Fetch vehicle documents');
    }
  },

  cancelBooking: async (bookingId, cancelledBy = 'USER') => {
    try {
      const response = await apiClient.post(
        `/api/booking-bikes/${bookingId}/cancel`,
        null,
        { params: { cancelledBy } }
      );
      return response.data;
    } catch (error) {
      handleApiError(error, 'Cancel booking');
    }
  },

  completeBooking: async (bookingId) => {
    try {
      const response = await apiClient.post(`/api/booking-bikes/${bookingId}/complete`);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Complete booking');
    }
  },

  getInvoice: async (bookingId) => {
    try {
      const response = await apiClient.get(
        `/api/booking-bikes/${bookingId}/invoice`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      handleApiError(error, 'Fetch invoice');
    }
  },

  checkDocumentVerification: async (userId) => {
    try {
      if (!userId) throw new Error('User ID is required');

      const tokenKey = STORAGE_KEYS.TOKEN;
      const token = localStorage.getItem(tokenKey);
      if (!token) throw new Error('Authentication required');

      const response = await apiClient.get(`/api/documents/${userId}`);
      console.log('📄 Document verification response:', response.data);

      const documents = response.data;
      
      if (!documents) {
        return {
          verified: false,
          uploaded: false,
          pending: false,
          rejected: false,
          message: 'No documents uploaded'
        };
      }

      const adhaarFrontVerified = documents.isAdhaarFrontVerified === 1;
      const adhaarBackVerified = documents.isAdhaarBackVerified === 1;
      const licenseVerified = documents.isLicenseVerified === 1;

      const adhaarFrontPending = documents.isAdhaarFrontVerified === 0;
      const adhaarBackPending = documents.isAdhaarBackVerified === 0;
      const licensePending = documents.isLicenseVerified === 0;

      const adhaarFrontRejected = documents.isAdhaarFrontVerified === 2;
      const adhaarBackRejected = documents.isAdhaarBackVerified === 2;
      const licenseRejected = documents.isLicenseVerified === 2;

      const adhaarFrontUploaded = documents.adhaarFrontImage?.trim();
      const adhaarBackUploaded = documents.adhaarBackImage?.trim();
      const licenseUploaded = documents.drivingLicenseImage?.trim();

      const allUploaded = adhaarFrontUploaded && adhaarBackUploaded && licenseUploaded;
      const isVerified = adhaarFrontVerified && adhaarBackVerified && licenseVerified;
      const isPending = adhaarFrontPending || adhaarBackPending || licensePending;
      const isRejected = adhaarFrontRejected || adhaarBackRejected || licenseRejected;

      return {
        verified: isVerified,
        uploaded: allUploaded,
        pending: isPending,
        rejected: isRejected,
        documents: documents,
        message: isVerified ? 'All documents verified' : 
                 isRejected ? 'Some documents rejected' : 
                 isPending ? 'Documents pending verification' : 
                 !allUploaded ? 'Upload all required documents' :
                 'Documents not verified'
      };

    } catch (error) {
      if (error.response?.status === 404) {
        return {
          verified: false,
          uploaded: false,
          pending: false,
          rejected: false,
          message: 'No documents uploaded'
        };
      }
      handleApiError(error, 'Check document verification');
    }
  },

  // ✅ NEW METHOD: Validate Coupon
  // Replace the validateCoupon method in bookingService.js

validateCoupon: async (couponCode, vehicleId, originalPrice) => {
  try {
    console.log('🎟️ Validating coupon:', couponCode);
    
    if (!couponCode || !vehicleId || !originalPrice) {
      throw new Error('Missing required parameters for coupon validation');
    }

    // ✅ UPDATED: Try multiple ways to get user ID
    let customerId = null;

    // Method 1: Try to get from auth_user
    const userStr = localStorage.getItem('auth_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        customerId = user?.data?.id || user?.id || user?.userId;
        console.log('✅ Found user from auth_user:', customerId);
      } catch (e) {
        console.warn('Failed to parse auth_user:', e);
      }
    }

    // Method 2: Try to decode from token if user not found
    if (!customerId) {
      const tokenKey = STORAGE_KEYS.TOKEN;
      const token = localStorage.getItem(tokenKey);
      
      if (token) {
        const decoded = decodeJWT(token);
        customerId = decoded?.customerId || decoded?.userId || decoded?.id || decoded?.sub;
        console.log('✅ Found user from token:', customerId);
      }
    }

    // Method 3: Check all localStorage keys for user data
    if (!customerId) {
      console.log('🔍 Searching all localStorage for user ID...');
      
      // Try common user storage keys
      const possibleKeys = ['user', 'userData', 'currentUser', 'authUser', 'customer', 'customerId'];
      
      for (const key of possibleKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            customerId = parsed?.id || parsed?.customerId || parsed?.userId || parsed?.data?.id;
            if (customerId) {
              console.log(`✅ Found user ID from ${key}:`, customerId);
              break;
            }
          } catch (e) {
            // Try as direct value
            if (!isNaN(value)) {
              customerId = parseInt(value);
              console.log(`✅ Found user ID from ${key} (direct):`, customerId);
              break;
            }
          }
        }
      }
    }

    // Final check
    if (!customerId) {
      console.error('❌ Could not find customer ID. LocalStorage contents:');
      console.log('Available keys:', Object.keys(localStorage));
      
      // Log all localStorage for debugging
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        console.log(`  ${key}:`, value?.substring(0, 100));
      }
      
      throw new Error('User information not found. Please login again.');
    }

    const payload = {
      couponCode: couponCode.trim().toUpperCase(),
      customerId: parseInt(customerId, 10),
      vehicleId: parseInt(vehicleId, 10),
      originalPrice: parseFloat(originalPrice)
    };

    console.log('🎟️ Coupon validation payload:', payload);

    // Use apiClient which already has auth headers from interceptor
    const response = await apiClient.post('/api/offers/validate', payload);
    
    console.log('✅ Coupon validation response:', response.data);
    return response.data;

  } catch (error) {
    handleApiError(error, 'Validate coupon');
  }
},


  validateBookingDates: (startDate, endDate, pickupTime, dropoffTime) => {
    const start = new Date(`${startDate}T${pickupTime}`);
    const end = new Date(`${endDate}T${dropoffTime}`);
    const now = new Date();

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error('Invalid date or time format');
    }

    if (start < now) {
      throw new Error('Start date and time cannot be in the past');
    }

    if (end <= start) {
      throw new Error('End date and time must be after start date and time');
    }

    const diffInHours = (end - start) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      throw new Error('Minimum rental duration is 1 hour');
    }

    if (diffInHours > 720) {
      throw new Error('Maximum rental duration is 30 days');
    }

    return Math.ceil(diffInHours);
  },

  calculateRentalPrice: (hourlyRate, totalHours) => {
    if (!hourlyRate || !totalHours) return 0;
    return Math.round(hourlyRate * totalHours);
  },

  formatPrice: (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  }
};

export default bookingService;
